import { hasBulletTargets } from './buildBulletSeries';
import { takesNoMetrics } from './legacyGadgetBlocks';
import { isMediaVisType, isTimelineHeatmap } from './visTypes';

export type SetupStep = {
  // The settings section it's done in
  id: Extract<Studio.SettingsSection, 'metrics' | 'dimensions' | 'gauge'>;
  label: string;
  isDone: boolean;
};

const DIMENSION_LABELS: Partial<Record<Studio.VisType, string>> = {
  pie: 'Add a dimension',
  radar: 'Add a dimension',
  area: 'Add a dimension',
  stackedBar: 'Add a dimension',
  funnel: 'Add a dimension',
  packedBubble: 'Add a dimension',
  scatter: 'Add a dimension',
  regionMap: 'Add a state or province dimension',
  heatmap: 'Add two dimensions',
  sankey: 'Add two dimensions (from and to)',
};

const TWO_DIMENSION_VIS_TYPES: Studio.VisType[] = ['heatmap', 'sankey'];

// What a chart block needs before it can draw, as the steps to set it up in
// its settings, each ticked once it's done. Mirrors the checks in
// buildChartDefinition and buildGaugeDefinition. Blocks that take no metrics
// (texts, Composites, records tables, legacy gadget blocks) have no steps.
const getSetupSteps = (
  card: Pick<
    Studio.Card,
    | 'visType'
    | 'description'
    | 'metricIds'
    | 'dimensions'
    | 'gauge'
    | 'table'
    | 'line'
  > &
    Partial<Pick<Studio.Card, 'chart'>>,
): SetupStep[] => {
  const { visType } = card;
  if (
    visType === 'text' ||
    visType === 'composite' ||
    visType === 'classic' ||
    isMediaVisType(visType) ||
    takesNoMetrics(card)
  ) {
    return [];
  }

  const metricCount = (card.metricIds ?? []).length;
  const dimensionCount = (card.dimensions ?? []).length;
  const metricsStep = ((): SetupStep => {
    if (visType === 'scatter') {
      return {
        id: 'metrics',
        label: 'Add two metrics (x and y)',
        isDone: metricCount >= 2,
      };
    }
    // Its target is in a metric's options
    if (visType === 'bullet') {
      return {
        id: 'metrics',
        label: 'Add a metric and set its target',
        isDone: metricCount > 0 && hasBulletTargets(card),
      };
    }
    return { id: 'metrics', label: 'Add a metric', isDone: metricCount > 0 };
  })();
  const steps: SetupStep[] = [metricsStep];

  // A Timeline heatmap's columns are dates: it takes no dimensions
  const dimensionLabel =
    visType && !isTimelineHeatmap(card) && DIMENSION_LABELS[visType];
  if (visType && dimensionLabel) {
    steps.push({
      id: 'dimensions',
      label: dimensionLabel,
      isDone:
        dimensionCount >= (TWO_DIMENSION_VIS_TYPES.includes(visType) ? 2 : 1),
    });
  }
  if (visType === 'gauge') {
    steps.push({
      id: 'gauge',
      label: 'Choose a peer group and entity',
      isDone: !!card.gauge?.peerGroup && !!card.gauge?.entity,
    });
  }
  return steps;
};

export const isSetUp = (steps: SetupStep[]): boolean =>
  steps.every((step) => step.isDone);

// A settings section's part in setting a block up: none, or whether it's
// still to do
export type SetupStepState = 'toDo' | 'done' | undefined;

export const getSetupStepState = ({
  steps,
  id,
}: {
  steps: SetupStep[];
  id: SetupStep['id'];
}): SetupStepState => {
  const step = steps.find((s) => s.id === id);
  if (!step) {
    return undefined;
  }
  return step.isDone ? 'done' : 'toDo';
};

export default getSetupSteps;
